import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Folder, File, Download, Search, ChevronRight, ChevronDown, ArrowLeft, Shield } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { parseUTC } from '../lib/date';

const SuperAdminFileManager = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = searchParams.get('token');
  
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderFiles, setFolderFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    loadFolders();
  }, [token]);

  const loadFolders = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/superadmin/file-manager/folders`,
        {
          headers: {
            'X-Super-Admin-Token': token
          }
        }
      );

      if (!response.ok) throw new Error('Failed to load folders');

      const data = await response.json();
      setFolders(data.folders);
    } catch (error) {
      console.error('Error loading folders:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les dossiers",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadFolderFiles = async (folderName) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/superadmin/file-manager/files/${encodeURIComponent(folderName)}`,
        {
          headers: {
            'X-Super-Admin-Token': token
          }
        }
      );

      if (!response.ok) throw new Error('Failed to load files');

      const data = await response.json();
      setFolderFiles(data.files);
      setSelectedFolder(folderName);
    } catch (error) {
      console.error('Error loading files:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les fichiers",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/superadmin/file-manager/download/${fileId}`,
        {
          headers: {
            'X-Super-Admin-Token': token
          }
        }
      );

      if (!response.ok) throw new Error('Failed to download file');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Succès",
        description: `${filename} téléchargé avec succès`,
      });
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: "Erreur",
        description: "Impossible de télécharger le fichier",
        variant: "destructive"
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return mb < 1 ? `${(bytes / 1024).toFixed(2)} KB` : `${mb.toFixed(2)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = parseUTC(dateString);
    return date.toLocaleString('fr-FR');
  };

  const getFileTypeLabel = (fileType, version) => {
    if (fileType === 'original') return 'Original';
    if (fileType === 'modified' && version) return version;
    return 'Fichier';
  };

  const filteredFolders = folders.filter(folder =>
    folder.folder_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#111111] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate(`/super-admin?token=${token}`)}
            variant="outline"
            className="mb-4 border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour au Dashboard
          </Button>
          
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-[#C00000]" />
            <h1 className="text-4xl font-bold">
              <span className="text-white">Gestionnaire de</span>{' '}
              <span className="text-[#C00000]">Fichiers</span>
            </h1>
          </div>
          <p className="text-[#A6A6A6] mt-2">Accès Super Admin - Tous les fichiers de la plateforme</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Liste des dossiers */}
          <div className="lg:col-span-1">
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Folder className="h-5 w-5 text-[#C00000]" />
                  Dossiers ({filteredFolders.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Recherche */}
                <div className="mb-4 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A6A6A6] h-4 w-4" />
                  <Input
                    type="text"
                    placeholder="Rechercher un dossier..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                  />
                </div>

                {/* Liste */}
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {loading && !selectedFolder ? (
                    <p className="text-[#A6A6A6] text-center py-4">Chargement...</p>
                  ) : filteredFolders.length === 0 ? (
                    <p className="text-[#A6A6A6] text-center py-4">Aucun dossier trouvé</p>
                  ) : (
                    filteredFolders.map((folder) => (
                      <button
                        key={folder.folder_name}
                        onClick={() => loadFolderFiles(folder.folder_name)}
                        className={`w-full text-left p-3 rounded-md transition-colors ${
                          selectedFolder === folder.folder_name
                            ? 'bg-[#C00000]/20 border border-[#C00000]'
                            : 'bg-[#111111] border border-[#A6A6A6]/20 hover:border-[#C00000]/50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-2 flex-1 min-w-0">
                            {selectedFolder === folder.folder_name ? (
                              <ChevronDown className="h-5 w-5 text-[#C00000] flex-shrink-0 mt-0.5" />
                            ) : (
                              <ChevronRight className="h-5 w-5 text-[#A6A6A6] flex-shrink-0 mt-0.5" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-sm font-medium truncate">
                                {folder.folder_name}
                              </p>
                              <p className="text-[#A6A6A6] text-xs mt-1">
                                {folder.file_count} fichier{folder.file_count > 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Fichiers du dossier sélectionné */}
          <div className="lg:col-span-2">
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <File className="h-5 w-5 text-[#C00000]" />
                  {selectedFolder ? `Fichiers de ${selectedFolder}` : 'Sélectionnez un dossier'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-[#A6A6A6] text-center py-8">Chargement des fichiers...</p>
                ) : !selectedFolder ? (
                  <p className="text-[#A6A6A6] text-center py-8">
                    Sélectionnez un dossier pour voir ses fichiers
                  </p>
                ) : folderFiles.length === 0 ? (
                  <p className="text-[#A6A6A6] text-center py-8">Aucun fichier dans ce dossier</p>
                ) : (
                  <div className="space-y-3">
                    {folderFiles.map((file) => (
                      <div
                        key={file.file_id}
                        className="flex justify-between items-center p-4 bg-[#111111] rounded-lg border border-[#A6A6A6]/20 hover:border-[#C00000]/50 transition-colors"
                      >
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <File className="h-5 w-5 text-[#C00000] flex-shrink-0 mt-1" />
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{file.filename}</p>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                              <span className="text-[#A6A6A6] text-xs">
                                Type: <span className="text-white">{getFileTypeLabel(file.file_type, file.version)}</span>
                              </span>
                              <span className="text-[#A6A6A6] text-xs">
                                Taille: <span className="text-white">{formatFileSize(file.size)}</span>
                              </span>
                              <span className="text-[#A6A6A6] text-xs">
                                Upload: <span className="text-white">{formatDate(file.uploaded_at)}</span>
                              </span>
                            </div>
                            {file.uploaded_by_name && (
                              <span className="text-[#A6A6A6] text-xs">
                                Par: <span className="text-white">{file.uploaded_by_name}</span>
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          onClick={() => downloadFile(file.file_id, file.filename)}
                          size="sm"
                          className="bg-[#C00000] hover:bg-[#C00000]/90 flex-shrink-0 ml-4"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Télécharger
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminFileManager;
